import server from 'api';
import {
  GroupCoupon,
  GroupFallbackResult,
  GroupFeedback,
  GroupMolliePayments,
  GroupMolliePaymentsAll,
  GroupReservations,
  GroupShop,
  GroupSummary,
  GroupUserRole,
  GroupVoice,
} from './types';

const BASE = '/v03/group';

// `server` is untyped (utils/server is plain JS), so the response type is
// declared on the helper instead of as a call type argument.
const data = <T>(response: { data: T }) => response.data;

// The api answers ExpectedErrors as { message, error: true }.
export const errorMessage = (error: any): string =>
  error?.response?.data?.message || error?.message || 'Request failed';

export const fetchGroup = (groupId: string): Promise<GroupSummary> =>
  server.get(`${BASE}/${groupId}`).then(data);

export const fetchGroupReservations = (
  groupId: string,
  from: string,
  till: string,
): Promise<GroupReservations> =>
  server.get(`${BASE}/${groupId}/reservations`, { params: { from, till } }).then(data);

export const fetchRestaurantMolliePayments = (
  groupId: string,
  restaurantId: string,
  from: string,
  till: string,
): Promise<GroupMolliePayments> =>
  server
    .get(`${BASE}/${groupId}/restaurant/${restaurantId}/mollie-payments`, {
      params: { from, till },
    })
    .then(data);

export const fetchGroupMolliePayments = (
  groupId: string,
  from: string,
  till: string,
): Promise<GroupMolliePaymentsAll> =>
  server.get(`${BASE}/${groupId}/mollie-payments`, { params: { from, till } }).then(data);

export const addGroupMember = (
  groupId: string,
  member: { email: string; name: string; role: GroupUserRole },
): Promise<{ inLocations: boolean }> => server.post(`${BASE}/${groupId}/members`, member).then(data);

export const removeGroupMember = (groupId: string, uid: string): Promise<void> =>
  server.delete(`${BASE}/${groupId}/members/${uid}`).then(data);

export const fetchGroupVoice = (groupId: string, from: string, till: string): Promise<GroupVoice> =>
  server.get(`${BASE}/${groupId}/voice`, { params: { from, till } }).then(data);

export const fetchGroupShop = (groupId: string, from: string, till: string): Promise<GroupShop> =>
  server.get(`${BASE}/${groupId}/shop`, { params: { from, till } }).then(data);

export const fetchGroupFeedback = (
  groupId: string,
  query: {
    from: string;
    till: string;
    restaurantId: string | null;
    stars: number | null;
    withComment: boolean;
    page: number;
    scores: boolean;
  },
): Promise<GroupFeedback> =>
  server
    .get(`${BASE}/${groupId}/feedback`, {
      params: {
        from: query.from,
        till: query.till,
        restaurantId: query.restaurantId ?? undefined,
        stars: query.stars ?? undefined,
        comment: query.withComment ? 'yes' : undefined,
        page: query.page,
        scores: query.scores ? undefined : 'no',
      },
    })
    .then(data);

export const fetchGroupCoupons = (groupId: string): Promise<{ coupons: GroupCoupon[] }> =>
  server.get(`${BASE}/${groupId}/coupons`).then(data);

export const createGroupCoupon = (
  groupId: string,
  coupon: {
    code: string;
    title?: string;
    discount: GroupCoupon['discount'];
    per?: 'perGroup' | 'perPerson';
    restaurantIds: string[];
    maxUses?: number;
    lastDateOfPurchase?: string;
  },
): Promise<GroupCoupon> => server.post(`${BASE}/${groupId}/coupons`, coupon).then(data);

export const enterGroupFallback = (groupId: string): Promise<{ results: GroupFallbackResult[] }> =>
  server.post(`${BASE}/${groupId}/fallback`).then(data);
